import {
  AuthResponseSchema,
  ExchangeChallengeSchema,
  ExchangeDirectionsResponseSchema,
  ExchangeOrderSchema,
  OperationListSchema,
  OperationSchema,
  OtpChallengeResponseSchema,
  PinSessionResponseSchema,
  UserProfileSchema,
  WalletResponseSchema,
  WithdrawalChallengeSchema,
} from '@ccex/shared';
import type {
  AuthResponse,
  CreateExchangeOrderRequest,
  CreateWithdrawalRequest,
  ExchangeChallenge,
  ExchangeDirectionsResponse,
  ExchangeOrder,
  ExchangeRecipientRequest,
  Locale,
  Operation,
  OperationList,
  OtpChallengeResponse,
  PinSessionResponse,
  SubmitEmailRequest,
  UserProfile,
  WalletResponse,
  WithdrawalChallenge,
} from '@ccex/shared';
import { z } from 'zod';
import { request } from './http';

/** Every endpoint the Mini App uses. Shapes come from @ccex/shared, so backend and frontend cannot drift silently. */
export const api = {
  loginWithInitData: (
    initData: string,
    deviceTimezone?: string,
    inviteToken?: string | null,
  ): Promise<AuthResponse> =>
    request({
      method: 'POST',
      path: '/auth/telegram',
      body: {
        initData,
        ...(deviceTimezone ? { deviceTimezone } : {}),
        ...(inviteToken ? { inviteToken } : {}),
      },
      schema: AuthResponseSchema,
    }),

  me: (token: string): Promise<UserProfile> =>
    request({ method: 'GET', path: '/me', token, schema: UserProfileSchema }),

  setLocale: (token: string, locale: Locale): Promise<UserProfile> =>
    request({
      method: 'PATCH',
      path: '/me/locale',
      body: { locale },
      token,
      schema: UserProfileSchema,
    }),

  setTimezone: (token: string, timezone: string | null): Promise<UserProfile> =>
    request({
      method: 'PATCH',
      path: '/me/timezone',
      body: { timezone },
      token,
      schema: UserProfileSchema,
    }),

  enablePin: (token: string, pin: string): Promise<PinSessionResponse> =>
    request({
      method: 'POST',
      path: '/me/pin',
      body: { pin },
      token,
      schema: PinSessionResponseSchema,
    }),

  changePin: (token: string, currentPin: string, pin: string): Promise<PinSessionResponse> =>
    request({
      method: 'PUT',
      path: '/me/pin',
      body: { currentPin, pin },
      token,
      schema: PinSessionResponseSchema,
    }),

  disablePin: (token: string, currentPin: string): Promise<PinSessionResponse> =>
    request({
      method: 'DELETE',
      path: '/me/pin',
      body: { currentPin },
      token,
      schema: PinSessionResponseSchema,
    }),

  verifyPin: (token: string, pin: string): Promise<PinSessionResponse> =>
    request({
      method: 'POST',
      path: '/me/pin/verify',
      body: { pin },
      token,
      schema: PinSessionResponseSchema,
    }),

  requestPinReset: (token: string): Promise<OtpChallengeResponse> =>
    request({
      method: 'POST',
      path: '/me/pin/reset/request',
      token,
      schema: OtpChallengeResponseSchema,
    }),

  confirmPinReset: (token: string, code: string): Promise<PinSessionResponse> =>
    request({
      method: 'POST',
      path: '/me/pin/reset/confirm',
      body: { code },
      token,
      schema: PinSessionResponseSchema,
    }),

  submitEmail: (token: string, body: SubmitEmailRequest): Promise<OtpChallengeResponse> =>
    request({
      method: 'POST',
      path: '/onboarding/email',
      body,
      token,
      schema: OtpChallengeResponseSchema,
    }),

  resendCode: (token: string): Promise<OtpChallengeResponse> =>
    request({
      method: 'POST',
      path: '/onboarding/resend',
      token,
      schema: OtpChallengeResponseSchema,
    }),

  verifyCode: (token: string, code: string): Promise<UserProfile> =>
    request({
      method: 'POST',
      path: '/onboarding/verify',
      body: { code },
      token,
      schema: UserProfileSchema,
    }),

  getWallet: (token: string): Promise<WalletResponse> =>
    request({ method: 'GET', path: '/wallet', token, schema: WalletResponseSchema }),

  retryProvisioning: (token: string): Promise<void> =>
    request({ method: 'POST', path: '/wallet/provisioning/retry', token, schema: z.void() }),

  createWithdrawal: (token: string, body: CreateWithdrawalRequest): Promise<WithdrawalChallenge> =>
    request({
      method: 'POST',
      path: '/wallet/withdrawals',
      body,
      token,
      schema: WithdrawalChallengeSchema,
    }),

  confirmWithdrawal: (token: string, id: string, code: string): Promise<Operation> =>
    request({
      method: 'POST',
      path: `/wallet/withdrawals/${id}/confirm`,
      body: { code },
      token,
      schema: OperationSchema,
    }),

  resendWithdrawalCode: (token: string, id: string): Promise<WithdrawalChallenge> =>
    request({
      method: 'POST',
      path: `/wallet/withdrawals/${id}/resend`,
      token,
      schema: WithdrawalChallengeSchema,
    }),

  cancelWithdrawal: (token: string, id: string): Promise<Operation> =>
    request({
      method: 'POST',
      path: `/wallet/withdrawals/${id}/cancel`,
      token,
      schema: OperationSchema,
    }),

  getOperations: (
    token: string,
    page: { limit?: number; cursor?: string | null },
  ): Promise<OperationList> =>
    request({
      method: 'GET',
      path: `/wallet/operations?${new URLSearchParams({
        ...(page.limit ? { limit: String(page.limit) } : {}),
        ...(page.cursor ? { cursor: page.cursor } : {}),
      })}`,
      token,
      schema: OperationListSchema,
    }),

  getOperation: (token: string, id: string): Promise<Operation> =>
    request({ method: 'GET', path: `/wallet/operations/${id}`, token, schema: OperationSchema }),

  getExchangeDirections: (token: string): Promise<ExchangeDirectionsResponse> =>
    request({
      method: 'GET',
      path: '/exchange/directions',
      token,
      schema: ExchangeDirectionsResponseSchema,
    }),

  createExchangeOrder: (token: string, body: CreateExchangeOrderRequest): Promise<ExchangeOrder> =>
    request({ method: 'POST', path: '/exchange/orders', body, token, schema: ExchangeOrderSchema }),

  setExchangeRecipient: (
    token: string,
    id: string,
    body: ExchangeRecipientRequest,
  ): Promise<ExchangeOrder> =>
    request({
      method: 'POST',
      path: `/exchange/orders/${id}/recipient`,
      body,
      token,
      schema: ExchangeOrderSchema,
    }),

  payExchangeOrder: (token: string, id: string): Promise<ExchangeChallenge> =>
    request({
      method: 'POST',
      path: `/exchange/orders/${id}/pay`,
      body: {},
      token,
      schema: ExchangeChallengeSchema,
    }),

  getExchangeOrder: (token: string, id: string): Promise<ExchangeOrder> =>
    request({ method: 'GET', path: `/exchange/orders/${id}`, token, schema: ExchangeOrderSchema }),
};
